/*
 * *****************************************************************************
 *     Copyright (C)  Motorola Solutions, INC.
 *     All Rights Reserved.
 *     Motorola Solutions Confidential Restricted.
 * *****************************************************************************
 */

import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Injectable } from '@angular/core';
import { ToastMode, ToastService } from '@msi/cobalt';
import { EnvironmentService } from './environment.service';

@Injectable({
    providedIn: 'root'
})
export class UpdateService {
    private notified = false;

    constructor(
        private updates: SwUpdate,
        private toastService: ToastService,
        private env: EnvironmentService
    ) {
        if (!this.updates.isEnabled) {
            console.info('Service worker updates are disabled.');
            return;
        }

        this.watchVersionEvents();

        navigator.serviceWorker.ready
            .then(() => console.info('Service worker is active. Checking for updates...'))
            .then(() => this.checkForUpdate(true))
            .then(() => this.updates.checkForUpdate())
            .then((hasUpdate) => (hasUpdate ? this.applyUpdate() : console.debug(`Client version is up to date. ${this.env.environment.BUILD_NUMBER}`)))
            .catch((e) => console.error('Service worker ready check failed. Software update Service offline.', e));
    }

    private watchVersionEvents(): void {
        this.updates.versionUpdates.subscribe({
            next: (event) => {
                if (event.type === 'VERSION_DETECTED') {
                    console.info('New client version detected by service worker.');
                }

                if (event.type === 'VERSION_READY' && !this.notified) {
                    const ready = event as VersionReadyEvent;
                    console.info(
                        `New client version ready. Current=${ready.currentVersion.hash}, Latest=${ready.latestVersion.hash}`
                    );
                    // this.showUpdateNotification();
                }
            },
            error: (e) => console.error('Failed to listen for service worker version events.', e)
        });
    }

    private checkForUpdate(initial: boolean): Promise<void> {
        return this.updates.checkForUpdate()
            .then((hasUpdate) => {
                if (hasUpdate && !this.notified) {
                    // this.showUpdateNotification();
                    return;
                }

                if (initial) {
                    console.info(`Client version is up to date. ${this.env.environment.BUILD_NUMBER}`);
                } else {
                    console.info('No new client version detected.');
                }
            })
            .catch((e) => console.error('Failed to check for client update.', e));
    }

    private showUpdateNotification() {
        console.debug(`New client version detected.`);
        this.toastService.warning("Update available", { click: () => this.applyUpdate(), text: 'Reload' }, {
            autoDismiss: false,
            closeButton: true,
            mode: ToastMode.Text
        });
        this.notified = true;
    }

    private applyUpdate() {
        console.debug('Client updates being applied.');
        this.updates.activateUpdate()
            .then((updated) => console.debug(`Client updates applied: ${updated} Reloading.`))
            .then(() => document.location.reload())
            .catch((e) => console.error('Client updates failed to apply', e));
    }
}
